import { UseLabelType } from "@apps/use-label/use-label";

export type RoutineTriggerType =
  | "time_of_day"
  | "before_meeting"
  | "after_meeting";

export type MeetingFilter = "all" | "oneonone_only" | "meeting_groups";

export type TimeOfDayConfig = {
  hour: number;
  // Python weekday() ints (0=Mon .. 6=Sun). Absent/empty = every day.
  days_of_week?: number[];
};

// 0=Mon .. 6=Sun, matching Python's date.weekday().
export const DAY_NAMES_SHORT = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];
export const DAY_NAMES_FULL = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/**
 * Human label for a days_of_week list (0=Mon..6=Sun). Empty/undefined -> ""
 * (meaning "every day"). [0-4] -> "weekdays", [5,6] -> "weekends", a single day
 * -> its full name, otherwise short names joined.
 */
export const formatDaysOfWeek = (days?: number[] | null): string => {
  if (!days || days.length === 0) return "";
  const ordered = [...new Set(days)]
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
    .sort((a, b) => a - b);
  if (ordered.length === 0) return "";
  if (ordered.join() === "0,1,2,3,4") return "weekdays";
  if (ordered.join() === "5,6") return "weekends";
  if (ordered.length === 1) return DAY_NAMES_FULL[ordered[0]];
  return ordered.map((d) => DAY_NAMES_SHORT[d]).join(", ");
};

export type MeetingTriggerConfig = {
  lead_time_minutes: number;
  meeting_filter: MeetingFilter;
  meeting_group_ids?: number[];
};

export type RoutineTriggerConfig =
  | TimeOfDayConfig
  | MeetingTriggerConfig
  | Record<string, unknown>;

/**
 * Format an integer hour (0-23) as a 12-hour clock label, e.g. 8 -> "8:00 AM",
 * 13 -> "1:00 PM", 0 -> "12:00 AM".
 */
export const formatHour = (hour: number): string => {
  const period = hour < 12 ? "AM" : "PM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:00 ${period}`;
};

/**
 * Turn a skill slug into a human-readable title, e.g. "team-updates" ->
 * "Team updates".
 */
export const humanizeSlug = (slug: string): string => {
  const words = (slug || "").replace(/[-_]+/g, " ").trim();
  if (!words) return "";
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * A short, human-readable description of when a routine runs.
 */
export const triggerSummary = (
  triggerType: string,
  config: RoutineTriggerConfig,
  label: UseLabelType,
): string => {
  if (triggerType === "time_of_day") {
    const { hour = 0, days_of_week } = config as TimeOfDayConfig;
    const days = formatDaysOfWeek(days_of_week);
    const cadence = days || "daily";
    return `${cadence} at ${formatHour(hour)}`;
  }

  if (triggerType === "before_meeting" || triggerType === "after_meeting") {
    const { lead_time_minutes = 0, meeting_filter = "all" } =
      config as MeetingTriggerConfig;
    const direction = triggerType === "before_meeting" ? "before" : "after";
    let scope = "all meetings";
    if (meeting_filter === "oneonone_only") {
      scope = `all ${label("oneonone", { pluralize: true })}`;
    } else if (meeting_filter === "meeting_groups") {
      scope = "selected meetings";
    }
    return `${lead_time_minutes} min ${direction} ${scope}`;
  }

  return "";
};

type RoutineLike = {
  name?: string | null;
  skillSlug: string;
};

/**
 * The display title for a routine: an explicit name wins, then the resolved
 * skill name, then a humanized version of the slug.
 */
export const routineTitle = (
  routine: RoutineLike,
  skillName?: string | null,
): string => {
  const trimmed = routine.name?.trim();
  if (trimmed) return trimmed;
  if (skillName) return skillName;
  return humanizeSlug(routine.skillSlug);
};

/**
 * Safely parse the JSON-encoded triggerConfig string coming from the backend.
 */
export const parseTriggerConfig = (
  triggerConfig: string,
): RoutineTriggerConfig => {
  if (!triggerConfig) return {};
  try {
    const parsed = JSON.parse(triggerConfig);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};
